import React, { useEffect, useState, useRef } from "react";
import { Box, Typography, Button, styled, IconButton } from "@mui/material";
import { useMediaQuery } from "@react-hook/media-query";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import theme from "../../theme";
import Api from "../../openapi";
import { PlanItemResponse, MealTypeResponse } from "../../openapi/client";
import { cdnUrl, findImageByType } from "../../utils";
import useMealTypes from "../../hooks/useMealTypes";
import usePartners from "../../hooks/usePartners";
import { useNavigate } from "react-router-dom";
import { MobileMealCard, DesktopMealCard, BrowseAllPlansCard } from "../MealCard";

// Styled Components
const MealsSection = styled(Box)(({ theme }) => ({
  padding: "48px 0", // Unified section padding
  background: "var(--body-bg)",
  display: "flex",
  justifyContent: "center",
  [theme.breakpoints.down("md")]: {
    padding: "32px 0", // Mobile section padding
  },
  [theme.breakpoints.down("sm")]: {
    padding: 0, // Small mobile section padding
  },
}));

const MealsContainer = styled(Box)(({ theme }) => ({
  width: "1109px",
  maxWidth: "100%",
  padding: "0 24px", // Unified container padding
  display: "flex",
  flexDirection: "column",
  gap: "24px", // Unified gap
  [theme.breakpoints.down("md")]: {
    padding: "0 20px", // Mobile container padding
    gap: "20px", // Mobile gap
  },
  [theme.breakpoints.down("sm")]: {
    padding: "0 16px", // Small mobile container padding
    gap: "16px", // Small mobile gap
  },
}));

const MealsHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  gap: "24px",
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
    gap: "16px",
  },
}));

// Navigation arrow overlay with glassmorphism effect
const NavigationOverlay = styled(IconButton)(({ theme }) => ({
  position: "absolute",
  top: "50%",
  transform: "translateY(-50%)",
  zIndex: 1000,
  width: "64px",
  height: "64px",
  background: "rgba(255, 255, 255, 0.85)",
  backdropFilter: "blur(12px)",
  border: "1px solid rgba(255, 255, 255, 0.2)",
  borderRadius: "20px",
  boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1), 0 2px 8px rgba(0, 0, 0, 0.05)",
  color: "#3e4044",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",

  "&:hover": {
    background: "rgba(255, 255, 255, 0.95)",
    transform: "translateY(-50%) scale(1.05)",
    boxShadow: "0 12px 48px rgba(0, 0, 0, 0.15), 0 4px 16px rgba(0, 0, 0, 0.1)",
  },

  "&:active": {
    transform: "translateY(-50%) scale(0.95)",
    transition: "all 0.1s ease",
  },

  "&.bounce-left": {
    animation: "bounceLeft 0.6s ease-out",
  },

  "&.bounce-right": {
    animation: "bounceRight 0.6s ease-out",
  },

  "&.disabled": {
    opacity: 0.4,
    pointerEvents: "none",
    "&:hover": {
      transform: "translateY(-50%)", // Remove scale on hover when disabled
      background: "rgba(255, 255, 255, 0.85)",
    },
  },

  "& .MuiSvgIcon-root": {
    fontSize: "32px",
    transition: "transform 0.2s ease",
  },

  "&:hover:not(.disabled) .MuiSvgIcon-root": {
    transform: "scale(1.1)",
  },

  // Hide on tablet and mobile
  [theme.breakpoints.down("lg")]: {
    display: "none",
  },

  // Keyframe animations
  "@keyframes bounceLeft": {
    "0%": { transform: "translateY(-50%) translateX(0)" },
    "50%": { transform: "translateY(-50%) translateX(-8px)" },
    "100%": { transform: "translateY(-50%) translateX(0)" },
  },

  "@keyframes bounceRight": {
    "0%": { transform: "translateY(-50%) translateX(0)" },
    "50%": { transform: "translateY(-50%) translateX(8px)" },
    "100%": { transform: "translateY(-50%) translateX(0)" },
  },
}));

// Left arrow positioned on the left side over gradient
const LeftArrow = styled(NavigationOverlay)(({ theme }) => ({
  insetInlineStart: "60px", // Positioned within the left gradient overlay
  [theme.breakpoints.up("md")]: {
    insetInlineStart: "80px",
  },
  [theme.breakpoints.up("lg")]: {
    insetInlineStart: "50px",
  },
}));

// Right arrow positioned on the right side over gradient
const RightArrow = styled(NavigationOverlay)(({ theme }) => ({
  insetInlineEnd: "60px", // Positioned within the right gradient overlay
  [theme.breakpoints.up("md")]: {
    insetInlineEnd: "80px",
  },
  [theme.breakpoints.up("lg")]: {
    insetInlineEnd: "50px",
  },
}));

// MealCard Component - Wrapper for Mobile/Desktop components
interface MealCardProps {
  meal?: PlanItemResponse;
  title?: string;
  subtitle?: string;
  mealTypes?: string;
  features?: string[];
  price?: string;
  partner?: string;
  imageUrl?: string;
  mealTypeList?: MealTypeResponse[];
}

const MealCardComponent: React.FC<MealCardProps> = ({
  meal,
  title,
  subtitle,
  mealTypes,
  features = [],
  price,
  partner,
  imageUrl,
  mealTypeList,
}) => {
  const mobile = useMediaQuery("only screen and (max-width: 600px)");

  // Prepare image URL
  const preparedImageUrl = meal
    ? (() => {
        const thumbnailUrl = findImageByType(meal.images, "THUMBNAIL");
        if (thumbnailUrl) {
          return `${cdnUrl}${thumbnailUrl}`;
        }
        const partnerSlug = meal?.partner?.slug;
        if (partnerSlug) {
          return `../../../${partnerSlug}/no-image.svg`;
        }
        return imageUrl;
      })()
    : imageUrl;

  if (mobile) {
    return (
      <MobileMealCard
        meal={meal}
        title={title}
        subtitle={subtitle}
        features={features}
        price={price}
        partner={partner}
        imageUrl={preparedImageUrl}
      />
    );
  }

  return (
    <DesktopMealCard
      meal={meal}
      title={title}
      subtitle={subtitle}
      mealTypes={mealTypes}
      features={features}
      price={price}
      partner={partner}
      imageUrl={preparedImageUrl}
    />
  );
};

const MealsSectionComponent: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const partners = usePartners();
  const mealTypeList = useMealTypes();
  const [meals, setMeals] = useState<PlanItemResponse[]>([]);
  const [selectedPartner, setSelectedPartner] = useState<string>("All");

  // Navigation arrow state management
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [leftArrowAnimation, setLeftArrowAnimation] = useState("");
  const [rightArrowAnimation, setRightArrowAnimation] = useState("");

  useEffect(() => {
    const getMeals = async () => {
      const res = await Api.Plan.listFeaturedPlans();
      setMeals(res.data);
    };
    getMeals();
  }, []);

  // Filter meals based on selected partner
  const filteredMeals =
    selectedPartner === "All" ? meals : meals.filter((meal) => meal.partner?.name === selectedPartner);

  const handlePartnerFilter = (partnerName: string) => {
    setSelectedPartner(partnerName);
  };

  // Check scroll position and update arrow states
  const updateScrollState = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      const isRTL = i18n.language === "ar";

      if (isRTL) {
        // In RTL, scrollLeft is negative when scrolled right, 0 when at leftmost
        const maxScrollLeft = -(scrollWidth - clientWidth);
        setCanScrollLeft(scrollLeft < -1); // Can scroll left (visually right) if scrollLeft is negative
        setCanScrollRight(scrollLeft > maxScrollLeft + 1); // Can scroll right (visually left) if not at max negative
      } else {
        setCanScrollLeft(scrollLeft > 0);
        setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
      }
    }
  };

  // Smooth scroll left
  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      const cardWidth = 380; // Meal card width on desktop
      const gap = 24; // Gap between cards
      const scrollDistance = cardWidth + gap;
      const isRTL = i18n.language === "ar";

      scrollContainerRef.current.scrollBy({
        left: isRTL ? scrollDistance : -scrollDistance,
        behavior: "smooth",
      });

      // Add bounce animation
      setLeftArrowAnimation("bounce-left");
      setTimeout(() => setLeftArrowAnimation(""), 600);
    }
  };

  // Smooth scroll right
  const scrollRight = () => {
    if (scrollContainerRef.current) {
      const cardWidth = 380; // Meal card width on desktop
      const gap = 24; // Gap between cards
      const scrollDistance = cardWidth + gap;
      const isRTL = i18n.language === "ar";

      scrollContainerRef.current.scrollBy({
        left: isRTL ? -scrollDistance : scrollDistance,
        behavior: "smooth",
      });

      // Add bounce animation
      setRightArrowAnimation("bounce-right");
      setTimeout(() => setRightArrowAnimation(""), 600);
    }
  };

  // Setup scroll listeners
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      // Initial scroll state check
      updateScrollState();

      // Add scroll listener
      container.addEventListener("scroll", updateScrollState);

      return () => {
        container.removeEventListener("scroll", updateScrollState);
      };
    }
  }, []);

  // Update scroll state when meals are loaded or filtered
  useEffect(() => {
    const timer = setTimeout(() => {
      updateScrollState();
    }, 100); // Small delay to ensure DOM is updated

    return () => clearTimeout(timer);
  }, [filteredMeals]);

  return (
    <MealsSection>
      <MealsContainer>
        {/* Header Section */}
        <MealsHeader>
          <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            <Typography
              sx={{
                fontFamily: "Inter",
                fontWeight: 600,
                fontSize: ["20px", "32px"],
                lineHeight: "normal",
                color: "var(--font-dark)",
              }}
            >
              {t("common.mealsSection.title")}
            </Typography>
            <Typography
              sx={{
                fontFamily: "Inter",
                fontWeight: 400,
                fontSize: ["14px", "18px"],
                lineHeight: "1.5",
                color: "var(--font-dark)",
              }}
            >
              {t("common.mealsSection.description")}
            </Typography>
          </Box>
          <Button
            onClick={() => navigate("/all-plans")}
            sx={{
              background: "var(--primary-lighter)",
              color: "var(--primary-dark)",
              padding: "12px 16px",
              borderRadius: "12px",
              fontFamily: "Inter",
              fontWeight: 600,
              fontSize: "14px",
              lineHeight: "20px",
              textTransform: "none",
              height: "48px",
              flexShrink: 0,
              display: { xs: "none", md: "block" },
              "&:hover": {
                background: "var(--primary-lighter)",
              },
            }}
          >
            {t("common.mealsSection.browseAllPlans.title")}
          </Button>
        </MealsHeader>
        {/* Hide this partner selection filter for now */}
        {/* Filter Buttons */}
        {/* <Box
          sx={{
            display: "flex",
            gap: "12px",
            flexWrap: "wrap",
            mb: { xs: 0, md: 4 },
            // Mobile horizontal scrolling
            [theme.breakpoints.down("md")]: {
              flexWrap: "nowrap",
              overflowX: "auto",
              scrollbarWidth: "none", // Firefox
              msOverflowStyle: "none", // IE/Edge
              "&::-webkit-scrollbar": {
                display: "none", // Chrome/Safari/Webkit
              },
              paddingBottom: "8px", // Add padding for better touch target
              marginX: "-24px", // Extend to container edges
              paddingX: "24px", // Restore internal padding
            },
          }}
        >
          <Button
            onClick={() => handlePartnerFilter("All")}
            sx={{
              background: selectedPartner === "All" ? "var(--primary-default)" : "transparent",
              color: selectedPartner === "All" ? "white" : "var(--font-dark)",
              padding: ["8px 16px", "16px 24px"],
              borderRadius: "12px",
              border: selectedPartner === "All" ? "none" : "1px solid var(--base-100)",
              fontFamily: "Inter",
              fontWeight: selectedPartner === "All" ? 600 : 500,
              fontSize: ["14px", "16px"],
              textTransform: "none",
              minWidth: "auto",
              flexShrink: 0,
              "&:hover": {
                background: selectedPartner === "All" ? "var(--primary-default)" : "var(--base-50)",
              },
            }}
          >
            {t("common.mealsSection.allFilter")}
          </Button>
          
          {partners
            ?.filter((partner) => partner.active)
            .map((partner) => (
              <Button
                key={partner.id}
                onClick={() => handlePartnerFilter(partner.name || "")}
                sx={{
                  background: selectedPartner === partner.name ? "var(--primary-default)" : "transparent",
                  color: selectedPartner === partner.name ? "white" : "var(--font-dark)",
                  padding: ["8px 16px", "16px 24px"],
                  borderRadius: "12px",
                  border: selectedPartner === partner.name ? "none" : "1px solid var(--base-100)",
                  fontFamily: "Inter",
                  fontWeight: selectedPartner === partner.name ? 600 : 500,
                  fontSize: ["14px", "16px"],
                  textTransform: "none",
                  minWidth: "auto",
                  flexShrink: 0, // Prevent shrinking in scroll
                  "&:hover": {
                    background: selectedPartner === partner.name ? "var(--primary-default)" : "var(--base-50)",
                  },
                }}
              >
                {partner.name}
              </Button>
            ))}
        </Box> */}

        {/* Meal Cards Container */}
        <Box
          sx={{
            position: "relative",
            width: "100vw",
            marginInlineStart: "calc(-50vw + 50%)",
            paddingBottom: "24px",
            overflow: "visible",
          }}
        >
          <Box
            ref={scrollContainerRef}
            sx={{
              display: "flex",
              alignItems: "stretch",
              gap: { xs: "16px", sm: "20px", md: "24px" },
              overflowX: "auto",
              overflowY: "visible",
              paddingInlineStart: { xs: "16px", sm: "40px", md: "60px", lg: "80px" },
              paddingInlineEnd: { xs: "16px", sm: "40px", md: "60px", lg: "80px" },
              paddingBottom: "12px",
              position: "relative",
              width: "100%",
              "&::-webkit-scrollbar": {
                display: "none",
              },
              scrollbarWidth: "none",
              msOverflowStyle: "none",
              scrollBehavior: "smooth",
            }}
          >
            {filteredMeals.map((meal, index) => (
              <MealCardComponent key={`${meal.slug}-${index}`} meal={meal} mealTypeList={mealTypeList} />
            ))}
            {/* Browse All Plans Card - Always shown as last item */}
            <BrowseAllPlansCard />
          </Box>

          {/* Start Gradient Overlay - Hidden on Mobile */}
          <Box
            sx={{
              position: "absolute",
              insetInlineStart: 0,
              top: 0,
              height: "100%",
              width: { xs: "0px", sm: "120px", md: "160px", lg: "200px" },
              background:
                i18n.language === "ar"
                  ? "linear-gradient(to left, #ffffff 0%, rgba(255,255,255,0) 100%)"
                  : "linear-gradient(to right, #ffffff 0%, rgba(255,255,255,0) 100%)",
              pointerEvents: "none",
              zIndex: 10,
              display: { xs: "none", sm: "block" },
            }}
          />

          {/* End Gradient Overlay - Hidden on Mobile */}
          <Box
            sx={{
              position: "absolute",
              insetInlineEnd: 0,
              top: 0,
              height: "100%",
              width: { xs: "0px", sm: "120px", md: "160px", lg: "200px" },
              background:
                i18n.language === "ar"
                  ? "linear-gradient(to right, #ffffff 0%, rgba(255,255,255,0) 100%)"
                  : "linear-gradient(to left, #ffffff 0%, rgba(255,255,255,0) 100%)",
              pointerEvents: "none",
              zIndex: 10,
              display: { xs: "none", sm: "block" },
            }}
          />

          {/* Navigation Overlay Arrows */}
          <LeftArrow
            className={`${leftArrowAnimation} ${!canScrollLeft ? "disabled" : ""}`}
            onClick={scrollLeft}
            aria-label={t("common.mealsSection.scrollLeftLabel")}
          >
            {i18n.language === "ar" ? <ChevronRight /> : <ChevronLeft />}
          </LeftArrow>

          <RightArrow
            className={`${rightArrowAnimation} ${!canScrollRight ? "disabled" : ""}`}
            onClick={scrollRight}
            aria-label={t("common.mealsSection.scrollRightLabel")}
          >
            {i18n.language === "ar" ? <ChevronLeft /> : <ChevronRight />}
          </RightArrow>
        </Box>
      </MealsContainer>
    </MealsSection>
  );
};

export default MealsSectionComponent;
export { MealCardComponent };
